import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { User, Target, FileText, ArrowRight, MapPin, DollarSign, TrendingUp, Heart, ThumbsUp, Edit3, Upload, X, Camera, Home, Bed, Bath } from 'lucide-react';
import { ActionItems, ActivityFeed } from '@/components/ActivityFeed';
import ScoreExplainer from '@/components/ScoreExplainer';

const STATUS_COLORS = {
    lead: 'bg-zinc-100 text-zinc-600',
    matched: 'bg-blue-100 text-blue-700',
    negotiation: 'bg-amber-100 text-amber-700',
    finalized: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-purple-100 text-purple-700',
};

export default function DashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [matches, setMatches] = useState([]);
    const [deals, setDeals] = useState([]);
    const [crProfile, setCrProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const endpoint = user.role === 'buyer' ? '/buyers/me' : '/sellers/me';
                const [profileRes, matchesRes, dealsRes] = await Promise.all([
                    API.get(endpoint),
                    API.get('/matching/suggestions'),
                    API.get('/deals')
                ]);
                setProfile(profileRes.data);
                setMatches(matchesRes.data);
                setDeals(dealsRes.data);
                try {
                    const crRes = await API.get('/credit-repair/profile');
                    setCrProfile(crRes.data);
                } catch (e) { /* not enrolled */ }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [user.role]);

    if (loading) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    const hasProfile = profile && profile.status !== 'no_profile';
    const isBuyer = user.role === 'buyer';

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="dashboard-page">
            <Navbar variant="light" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-medium text-zinc-900 font-['Outfit']">Welcome, {user.name}</h1>
                        <p className="text-sm text-zinc-500 mt-1">{isBuyer ? 'Buyer' : 'Seller'} Dashboard</p>
                    </div>
                    {!hasProfile && (
                        <Button data-testid="dashboard-intake-btn" onClick={() => navigate(isBuyer ? '/buyer/intake' : '/seller/intake')}
                            className={isBuyer ? 'bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6' : 'bg-amber-600 hover:bg-amber-700 text-white rounded-full px-6'}>
                            Complete Your Profile <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    )}
                </div>

                {/* Smart Action Items */}
                <ActionItems />

                <Tabs defaultValue="profile" className="space-y-6">
                    <TabsList className="bg-white border border-zinc-200 shadow-sm rounded-xl p-1">
                        <TabsTrigger value="profile" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-profile">
                            <User className="w-4 h-4 mr-1.5" /> Profile
                        </TabsTrigger>
                        <TabsTrigger value="matches" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-matches">
                            <Target className="w-4 h-4 mr-1.5" /> Matches ({matches.length})
                        </TabsTrigger>
                        <TabsTrigger value="deals" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-deals">
                            <FileText className="w-4 h-4 mr-1.5" /> Deals ({deals.length})
                        </TabsTrigger>
                        <TabsTrigger value="payments" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="tab-payments">
                            <DollarSign className="w-4 h-4 mr-1.5" /> Payments
                        </TabsTrigger>
                    </TabsList>

                    {/* PROFILE TAB */}
                    <TabsContent value="profile">
                        {!hasProfile ? (
                            <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center shadow-sm">
                                <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-4">
                                    <User className="w-8 h-8 text-zinc-400" />
                                </div>
                                <h3 className="text-lg font-medium text-zinc-900 mb-2 font-['Outfit']">Complete Your Profile</h3>
                                <p className="text-sm text-zinc-500 mb-6">
                                    {isBuyer ? 'Tell us about your financial profile to get pre-qualified.' : 'Tell us about your property to start receiving offers.'}
                                </p>
                                <Button data-testid="profile-start-intake" onClick={() => navigate(isBuyer ? '/buyer/intake' : '/seller/intake')}
                                    className={isBuyer ? 'bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8' : 'bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8'}>
                                    Start Intake <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </div>
                        ) : isBuyer ? (
                            <div className="space-y-6">
                                <BuyerProfile profile={profile} />
                                <ScoreExplainer />
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <SellerProfile profile={profile} />
                                <SellerTimeline profile={profile} />
                            </div>
                        )}
                    </TabsContent>

                    {/* MATCHES TAB */}
                    <TabsContent value="matches">
                        {matches.length === 0 ? (
                            <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center shadow-sm">
                                <Target className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-zinc-900 mb-2 font-['Outfit']">No Matches Yet</h3>
                                <p className="text-sm text-zinc-500">Our matching engine is working to find the best {isBuyer ? 'properties' : 'buyers'} for you.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {matches.map(m => (
                                    <MatchCard key={m.id} match={m} isBuyer={isBuyer} />
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* DEALS TAB */}
                    <TabsContent value="deals">
                        {deals.length === 0 ? (
                            <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center shadow-sm">
                                <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-zinc-900 mb-2 font-['Outfit']">No Deals Yet</h3>
                                <p className="text-sm text-zinc-500">Once matched and approved, your deals will appear here.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {deals.map(d => (
                                    <div key={d.id} className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm cursor-pointer hover:border-emerald-300 transition-colors"
                                        onClick={() => navigate(`/deals/${d.id}`)} data-testid={`deal-${d.id}`}>
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-medium text-zinc-900">{d.property_address}</span>
                                            <Badge className={STATUS_COLORS[d.status] || 'bg-zinc-100 text-zinc-600'}>
                                                {d.status?.charAt(0).toUpperCase() + d.status?.slice(1)}
                                            </Badge>
                                        </div>
                                        <div className="grid grid-cols-4 gap-4 text-center">
                                            <div>
                                                <div className="text-xs text-zinc-500">Down Payment</div>
                                                <div className="text-sm font-semibold text-zinc-900">${(d.down_payment || 0).toLocaleString()}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-zinc-500">Monthly</div>
                                                <div className="text-sm font-semibold text-zinc-900">${(d.monthly_payment || 0).toLocaleString()}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-zinc-500">Rate</div>
                                                <div className="text-sm font-semibold text-zinc-900">{((d.interest_rate || 0) * 100).toFixed(1)}%</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-zinc-500">Balloon</div>
                                                <div className="text-sm font-semibold text-zinc-900">${(d.balloon_amount || 0).toLocaleString()}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* PAYMENTS TAB */}
                    <TabsContent value="payments">
                        {deals.length === 0 ? (
                            <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center shadow-sm">
                                <DollarSign className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-zinc-900 mb-2 font-['Outfit']">No Payments Yet</h3>
                                <p className="text-sm text-zinc-500">Payment tracking will be available once you have active deals.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {deals.map(d => (
                                    <DealPaymentSummary key={d.id} deal={d} />
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>

                {/* Credit Repair CTA */}
                {isBuyer && (
                    <div className="mt-8 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 p-6 shadow-sm" data-testid="credit-repair-cta">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                                    <Heart className="w-6 h-6 text-emerald-700" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-zinc-900 font-['Outfit']">
                                        {crProfile && crProfile.status !== 'not_enrolled'
                                            ? `Credit Repair: ${crProfile.program_phase?.charAt(0).toUpperCase()}${crProfile.program_phase?.slice(1)} Phase`
                                            : 'Credit Repair Program'}
                                    </h3>
                                    <p className="text-sm text-zinc-500 mt-0.5">
                                        {crProfile && crProfile.status !== 'not_enrolled'
                                            ? `Score: ${crProfile.current_score_range} | Target: ${crProfile.target_score}`
                                            : 'Get mortgage-ready with our AI-powered credit repair program'}
                                    </p>
                                </div>
                            </div>
                            <Button onClick={() => navigate('/credit-repair')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-6" data-testid="dashboard-cr-btn">
                                {crProfile && crProfile.status !== 'not_enrolled' ? 'View Progress' : 'Get Started'} <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Activity Feed */}
                <div className="mt-6">
                    <ActivityFeed limit={8} />
                </div>
            </div>
        </div>
    );
}

function MatchCard({ match: m, isBuyer }) {
    const [interested, setInterested] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const expressInterest = async (e) => {
        e.stopPropagation();
        if (!isBuyer) return;
        setSubmitting(true);
        try {
            await API.post('/autonomy/interest', { listing_id: m.id });
            setInterested(true);
        } catch (err) {
            if (err.response?.status === 400) setInterested(true);
        } finally { setSubmitting(false); }
    };

    return (
        <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm hover:border-emerald-300 transition-colors" data-testid={`match-${m.id}`}>
            <div className="flex items-center justify-between mb-3">
                <Badge className="bg-emerald-100 text-emerald-700 font-semibold">{m.match_score}% Match</Badge>
                <span className="text-xs text-zinc-400">{m.status}</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4 text-zinc-400" />
                <span className="text-sm font-medium text-zinc-900">{m.property_address}</span>
            </div>
            {m.city && <div className="text-xs text-zinc-500 ml-6 mb-2">{m.city}, {m.state}</div>}
            <div className="flex items-center gap-4 mb-3 text-xs text-zinc-600">
                <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />${(m.asking_price || 0).toLocaleString()}</span>
                {m.bedrooms > 0 && <span>{m.bedrooms} bd / {m.bathrooms} ba</span>}
                {m.sqft > 0 && <span>{m.sqft?.toLocaleString()} sqft</span>}
                {m.property_type && <span className="capitalize">{m.property_type?.replace('_', ' ')}</span>}
            </div>
            {/* Match Reasoning */}
            {m.match_reasons?.length > 0 && (
                <div className="bg-emerald-50 rounded-lg p-3 mb-3 border border-emerald-200">
                    <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1">Why This Match</div>
                    <ul className="space-y-0.5">
                        {m.match_reasons.map((r, i) => (
                            <li key={i} className="text-xs text-emerald-800 flex items-start gap-1.5">
                                <span className="text-emerald-500 mt-0.5">+</span> {r}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {isBuyer && (
                <Button size="sm" onClick={expressInterest} disabled={interested || submitting}
                    className={interested ? 'bg-emerald-100 text-emerald-700 w-full text-xs' : 'bg-emerald-600 hover:bg-emerald-700 text-white w-full text-xs'}
                    data-testid={`interest-btn-${m.id}`}>
                    {interested ? <><ThumbsUp className="w-3 h-3 mr-1" /> Interest Submitted</> :
                     submitting ? 'Submitting...' : <><ThumbsUp className="w-3 h-3 mr-1" /> I'm Interested</>}
                </Button>
            )}
        </div>
    );
}

function BuyerProfile({ profile }) {
    const readinessPercent = Math.round((profile.readiness_score || 0) * 20);
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="buyer-profile">
            <div className="md:col-span-2 bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-zinc-900 font-['Outfit']">Your Profile</h3>
                    <Badge className={profile.status === 'pre_qualified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {profile.qualification}
                    </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    {[
                        ["Name", profile.full_name], ["Phone", profile.phone],
                        ["Income", profile.income_range], ["Credit Score", profile.credit_score_range],
                        ["Down Payment", `$${(profile.down_payment_available || 0).toLocaleString()}`],
                        ["Monthly Payment", `$${(profile.desired_monthly_payment || 0).toLocaleString()}`],
                        ["Location", profile.target_location], ["Timeline", profile.timeline_to_purchase],
                    ].map(([label, val]) => (
                        <div key={label}>
                            <div className="text-xs text-zinc-500 mb-0.5">{label}</div>
                            <div className="text-sm font-medium text-zinc-900">{val}</div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                    <div className="text-xs text-zinc-500 mb-2">Readiness Score</div>
                    <div className="text-3xl font-semibold text-emerald-700 mb-2 font-['Outfit']">{readinessPercent}%</div>
                    <Progress value={readinessPercent} className="h-2 bg-zinc-200" />
                </div>
                <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                    <div className="text-xs text-zinc-500 mb-2">Estimated Purchase Range</div>
                    <div className="text-lg font-semibold text-zinc-900">
                        ${(profile.est_purchase_low || 0).toLocaleString()} - ${(profile.est_purchase_high || 0).toLocaleString()}
                    </div>
                </div>
            </div>
        </div>
    );
}

function SellerProfile({ profile }) {
    const dom = profile.days_on_market || 0;
    const isActive = (profile.listing_status === 'active') || dom >= 91;
    const daysLeft = Math.max(0, 91 - dom);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [images, setImages] = useState(profile.images || []);
    const [uploading, setUploading] = useState(false);

    const startEdit = () => {
        setEditForm({
            description: profile.description || '', sqft: profile.sqft || '',
            bedrooms: profile.bedrooms || 3, bathrooms: profile.bathrooms || 2,
            year_built: profile.year_built || '', condition: profile.condition || 'good',
            features: (profile.features || []).join(', '), parking: profile.parking || '',
            hoa_monthly: profile.hoa_monthly || 0, owner_finance_terms: profile.owner_finance_terms || '',
            asking_price: profile.asking_price || '',
        });
        setEditing(true);
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            await API.put('/sellers/listing', {
                ...editForm,
                sqft: parseInt(editForm.sqft) || 0,
                bedrooms: parseInt(editForm.bedrooms) || 0,
                bathrooms: parseFloat(editForm.bathrooms) || 0,
                year_built: parseInt(editForm.year_built) || 0,
                hoa_monthly: parseFloat(editForm.hoa_monthly) || 0,
                asking_price: parseFloat(editForm.asking_price) || 0,
                features: editForm.features?.split(',').map(f => f.trim()).filter(Boolean) || [],
            });
            setEditing(false);
            window.location.reload();
        } catch (err) { console.error(err); }
        finally { setSaving(false); }
    };

    const uploadImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const { data } = await API.post('/sellers/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setImages(prev => [...prev, data]);
        } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); e.target.value = ''; }
    };

    const deleteImage = async (imgId) => {
        try {
            await API.delete(`/sellers/images/${imgId}`);
            setImages(prev => prev.filter(i => i.id !== imgId));
        } catch (err) { console.error(err); }
    };

    return (
        <div className="space-y-6" data-testid="seller-profile">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-zinc-900 font-['Outfit']">Your Property</h3>
                        <div className="flex items-center gap-2">
                            <Badge className={profile.motivation_tag === 'high' ? 'bg-emerald-100 text-emerald-700' : profile.motivation_tag === 'moderate' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'}>
                                {profile.motivation_tag?.charAt(0).toUpperCase() + profile.motivation_tag?.slice(1)} Motivation
                            </Badge>
                            <Button size="sm" variant="outline" onClick={editing ? saveEdit : startEdit} disabled={saving}
                                data-testid="seller-edit-btn"
                                className="text-xs border-zinc-200 h-7">
                                {editing ? (saving ? 'Saving...' : 'Save Changes') : <><Edit3 className="w-3 h-3 mr-1" /> Edit</>}
                            </Button>
                            {editing && <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="text-xs h-7 text-zinc-400">Cancel</Button>}
                        </div>
                    </div>

                    {/* Day 91 Status */}
                    <div className={`rounded-xl p-3 mb-4 flex items-center gap-3 ${isActive ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                            <span className={`text-sm font-bold ${isActive ? 'text-emerald-700' : 'text-amber-700'}`}>{isActive ? '91+' : dom}</span>
                        </div>
                        <div>
                            <div className={`text-sm font-semibold ${isActive ? 'text-emerald-700' : 'text-amber-700'}`}>
                                {isActive ? 'Active on Marketplace' : `${daysLeft} Days Until Marketplace Activation`}
                            </div>
                            <div className={`text-xs ${isActive ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {isActive ? 'Operators are matching buyers to your property.' : 'Your listing auto-activates at Day 91.'}
                            </div>
                        </div>
                    </div>

                    {!editing ? (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                                {[
                                    ["Address", profile.property_address],
                                    ["City/State", `${profile.city || ''}, ${profile.state || ''} ${profile.zip_code || ''}`],
                                    ["Asking Price", `$${(profile.asking_price || 0).toLocaleString()}`],
                                    ["Bedrooms", profile.bedrooms || 'N/A'],
                                    ["Bathrooms", profile.bathrooms || 'N/A'],
                                    ["Sq Ft", profile.sqft ? `${profile.sqft.toLocaleString()} sqft` : 'N/A'],
                                    ["Year Built", profile.year_built || 'N/A'],
                                    ["Condition", (profile.condition || '').replace('_', ' ').charAt(0).toUpperCase() + (profile.condition || '').slice(1)],
                                    ["Property Type", (profile.property_type || '').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())],
                                    ["Lot Size", profile.lot_size || 'N/A'],
                                    ["Parking", profile.parking || 'N/A'],
                                    ["HOA", profile.hoa_monthly ? `$${profile.hoa_monthly}/mo` : 'None'],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <div className="text-xs text-zinc-500 mb-0.5">{label}</div>
                                        <div className="text-sm font-medium text-zinc-900">{val}</div>
                                    </div>
                                ))}
                            </div>
                            {profile.description && (
                                <div className="mb-4">
                                    <div className="text-xs text-zinc-500 mb-1">Description</div>
                                    <p className="text-sm text-zinc-700">{profile.description}</p>
                                </div>
                            )}
                            {profile.features?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-4">
                                    {profile.features.map((f, i) => (
                                        <span key={i} className="text-xs bg-emerald-50 text-emerald-700 rounded-full px-2.5 py-0.5 border border-emerald-200">{f}</span>
                                    ))}
                                </div>
                            )}
                            {profile.owner_finance_terms && (
                                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                    <div className="text-xs font-bold text-blue-700 mb-1">Owner Finance Terms</div>
                                    <p className="text-xs text-blue-800">{profile.owner_finance_terms}</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="space-y-3" data-testid="seller-edit-form">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div><label className="text-xs text-zinc-500">Asking Price ($)</label><input type="number" value={editForm.asking_price} onChange={e => setEditForm(f => ({...f, asking_price: e.target.value}))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm" /></div>
                                <div><label className="text-xs text-zinc-500">Bedrooms</label><input type="number" value={editForm.bedrooms} onChange={e => setEditForm(f => ({...f, bedrooms: e.target.value}))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm" /></div>
                                <div><label className="text-xs text-zinc-500">Bathrooms</label><input type="number" step="0.5" value={editForm.bathrooms} onChange={e => setEditForm(f => ({...f, bathrooms: e.target.value}))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm" /></div>
                                <div><label className="text-xs text-zinc-500">Sq Ft</label><input type="number" value={editForm.sqft} onChange={e => setEditForm(f => ({...f, sqft: e.target.value}))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm" /></div>
                            </div>
                            <div><label className="text-xs text-zinc-500">Description</label><textarea value={editForm.description} onChange={e => setEditForm(f => ({...f, description: e.target.value}))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm min-h-16" /></div>
                            <div><label className="text-xs text-zinc-500">Features (comma-separated)</label><input value={editForm.features} onChange={e => setEditForm(f => ({...f, features: e.target.value}))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm" /></div>
                            <div><label className="text-xs text-zinc-500">Owner Finance Terms</label><textarea value={editForm.owner_finance_terms} onChange={e => setEditForm(f => ({...f, owner_finance_terms: e.target.value}))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm min-h-12" /></div>
                        </div>
                    )}
                </div>

                {/* Financials sidebar */}
                <div className="space-y-4">
                    <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2"><TrendingUp className="w-3 h-3" /> Projected Monthly</div>
                        <div className="text-2xl font-semibold text-emerald-700 font-['Outfit']">${(profile.projected_monthly_income || 0).toLocaleString()}</div>
                    </div>
                    <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                        <div className="text-xs text-zinc-500 mb-2">12-Month Return</div>
                        <div className="text-lg font-semibold text-emerald-700">${(profile.projected_total_return || 0).toLocaleString()}</div>
                    </div>
                    <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                        <div className="text-xs text-red-500 mb-2">12-Month Holding Loss</div>
                        <div className="text-lg font-semibold text-red-600">${(profile.total_loss_12mo || 0).toLocaleString()}</div>
                    </div>
                </div>
            </div>

            {/* Photos section */}
            <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm" data-testid="seller-photos-section">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-medium text-zinc-900 font-['Outfit']">Property Photos</h3>
                    <label className="cursor-pointer">
                        <input type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="seller-upload-photo" />
                        <Button asChild size="sm" variant="outline" className="text-xs border-zinc-200 h-7 cursor-pointer">
                            <span>{uploading ? 'Uploading...' : <><Camera className="w-3 h-3 mr-1" /> Add Photo</>}</span>
                        </Button>
                    </label>
                </div>
                {images.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {images.map(img => (
                            <div key={img.id} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-zinc-200 group">
                                <img src={img.data_url} alt="" className="w-full h-full object-cover" />
                                <button onClick={() => deleteImage(img.id)}
                                    className="absolute top-1 right-1 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <X className="w-3 h-3 text-white" />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 bg-zinc-50 rounded-xl border border-zinc-200">
                        <Camera className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                        <p className="text-sm text-zinc-500">No photos yet</p>
                        <p className="text-xs text-zinc-400 mt-0.5">Add photos to attract more qualified buyers</p>
                    </div>
                )}
            </div>
        </div>
    );
}


function SellerTimeline({ profile }) {
    const dom = profile.days_on_market || 0;
    const steps = [
        { label: 'Submitted', done: true, desc: 'Property listed on VisionAire' },
        { label: 'Agent Verified', done: dom > 0, desc: 'Agent enrolled your listing' },
        { label: 'Day 91 Active', done: dom >= 91, desc: dom >= 91 ? 'Live on marketplace' : `${Math.max(0, 91 - dom)} days remaining` },
        { label: 'Buyer Matched', done: profile.listing_status === 'matched' || profile.listing_status === 'closed', desc: 'Qualified buyer found' },
        { label: 'Closing', done: profile.listing_status === 'closed', desc: 'Deal finalized' },
    ];

    return (
        <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm" data-testid="seller-timeline">
            <h3 className="text-base font-medium text-zinc-900 font-['Outfit'] mb-4">Your Journey</h3>
            <div className="flex items-center gap-1">
                {steps.map((s, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center text-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-2 ${s.done ? 'bg-emerald-600 text-white' : 'bg-zinc-100 text-zinc-400'}`}>
                            {s.done ? '\u2713' : i + 1}
                        </div>
                        <div className={`text-[11px] font-medium ${s.done ? 'text-emerald-700' : 'text-zinc-500'}`}>{s.label}</div>
                        <div className="text-[10px] text-zinc-400 mt-0.5">{s.desc}</div>
                        {i < steps.length - 1 && (
                            <div className={`hidden`} />
                        )}
                    </div>
                ))}
            </div>
            {/* Progress bar connecting steps */}
            <div className="mt-3 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${(steps.filter(s => s.done).length / steps.length) * 100}%` }} />
            </div>
        </div>
    );
}


function DealPaymentSummary({ deal }) {
    const navigate = useNavigate();
    const [payments, setPayments] = useState(null);

    useEffect(() => {
        API.get(`/payments/${deal.id}`).then(r => setPayments(r.data)).catch(() => {});
    }, [deal.id]);

    const summary = payments?.summary;
    const paidPercent = summary ? Math.round((summary.paid_months / summary.total_months) * 100) : 0;

    return (
        <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm cursor-pointer hover:border-emerald-300 transition-colors"
            onClick={() => navigate(`/deals/${deal.id}`)} data-testid={`payment-deal-${deal.id}`}>
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-sm font-medium text-zinc-900">{deal.property_address}</div>
                    <div className="text-xs text-zinc-500">${(deal.monthly_payment || 0).toLocaleString()}/mo</div>
                </div>
                <Badge className={STATUS_COLORS[deal.status] || 'bg-zinc-100 text-zinc-600'}>
                    {deal.status?.charAt(0).toUpperCase() + deal.status?.slice(1)}
                </Badge>
            </div>
            {summary && (
                <div>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-zinc-500">{summary.paid_months} of {summary.total_months} payments</span>
                        <span className="text-emerald-700 font-semibold">{paidPercent}%</span>
                    </div>
                    <Progress value={paidPercent} className="h-2 bg-zinc-200" />
                    <div className="grid grid-cols-3 gap-4 mt-3 text-center text-xs">
                        <div><span className="text-zinc-500">Paid</span><div className="font-semibold text-emerald-700">${summary.total_paid.toLocaleString()}</div></div>
                        <div><span className="text-zinc-500">Remaining</span><div className="font-semibold text-zinc-900">${summary.total_remaining.toLocaleString()}</div></div>
                        <div><span className="text-zinc-500">Late Fees</span><div className={`font-semibold ${summary.total_late_fees > 0 ? 'text-red-600' : 'text-zinc-900'}`}>${summary.total_late_fees.toLocaleString()}</div></div>
                    </div>
                </div>
            )}
        </div>
    );
}
